import type { AvgSection } from '@/types/pages/AvgContent.ts'

const sections: AvgSection[] = [
  {
    title: 'A. Traitement des données à caractère personnel',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo traite des données à caractère personnel pour le compte du Restaurant dans le cadre de l’exécution du Contrat, notamment le traitement des commandes, des paiements et la prestation de services. Des données anonymisées sont également utilisées à des fins statistiques.',
      },
      {
        type: 'paragraph',
        text: 'Les données traitées comprennent notamment : nom, adresse, adresse e-mail, numéro de téléphone, données de paiement, adresse IP, ainsi que des données relatives à l’appareil et au navigateur.',
      },
      {
        type: 'paragraph',
        text: 'Bistroo agit en qualité de Sous-traitant, le Restaurant en qualité de Responsable du traitement.',
      },
    ],
  },
  {
    title: '1. Définitions',
    blocks: [
      {
        type: 'paragraph',
        text: 'Les présentes conditions (ci-après : les « Conditions de traitement des données ») utilisent un certain nombre de termes, dont la signification est précisée ci-dessous. Ces termes sont écrits avec une majuscule dans les Conditions de traitement des données. La liste ci-dessous reprend en grande partie les définitions issues de la législation et de la réglementation en matière de protection des données.',
      },
      {
        type: 'definition',
        term: 'Personne concernée',
        text: 'La personne à laquelle se rapporte une donnée à caractère personnel.',
      },
      {
        type: 'definition',
        term: 'Sous-traitant',
        text: 'Une personne physique ou morale, une autorité publique, un service ou un autre organisme qui traite des données à caractère personnel pour le compte du Responsable du traitement, sans être soumis à son autorité directe.',
      },
      {
        type: 'definition',
        term: 'Sous-traitant ultérieur',
        text: 'Un autre sous-traitant auquel le Sous-traitant fait appel pour mener des activités de traitement spécifiques pour le compte du Responsable du traitement.',
      },
      {
        type: 'definition',
        term: 'Responsable du traitement',
        text: 'Une personne physique ou morale, une autorité publique, un service ou un autre organisme qui, seul ou conjointement avec d’autres, détermine les finalités et les moyens du traitement des données à caractère personnel.',
      },
      {
        type: 'definition',
        term: 'Catégories particulières de données à caractère personnel',
        text: 'Les données qui révèlent l’origine raciale ou ethnique, les opinions politiques, les convictions religieuses ou philosophiques ou l’appartenance syndicale, ainsi que les données génétiques, les données biométriques aux fins d’identifier une personne de manière unique, les données concernant la santé, ou les données concernant la vie sexuelle ou l’orientation sexuelle d’une personne. Ainsi que les données à caractère personnel relatives aux condamnations pénales et aux infractions ou aux mesures de sûreté connexes.',
      },
      {
        type: 'definition',
        term: 'Fuite de données / Violation de données à caractère personnel',
        text: 'Une violation de la sécurité entraînant - ou dont on ne peut raisonnablement exclure qu’elle puisse entraîner -, de manière accidentelle ou illicite, la destruction, la perte, l’altération, la divulgation non autorisée de données à caractère personnel transmises, conservées ou traitées d’une autre manière, ou l’accès non autorisé à de telles données.',
      },
      {
        type: 'definition',
        term: 'Tiers',
        text: 'Toute personne autre que le Restaurant, Bistroo et leurs Collaborateurs.',
      },
      {
        type: 'definition',
        term: 'Obligation de notification des fuites de données',
        text: 'L’obligation de notifier les Fuites de données à l’autorité néerlandaise de protection des données (Autoriteit Persoonsgegevens) et (dans certains cas) à la ou aux Personne(s) concernée(s).',
      },
      {
        type: 'definition',
        term: 'Collaborateurs',
        text: 'Les personnes travaillant pour Bistroo ou pour le Restaurant, que ce soit en qualité de salarié ou dans le cadre d’une mission temporaire.',
      },
      {
        type: 'definition',
        term: 'Données à caractère personnel',
        text: 'Toute information se rapportant à une personne physique identifiée ou identifiable (la « Personne concernée ») traitée dans le cadre du Contrat ; est réputée identifiable une personne physique qui peut être identifiée, directement ou indirectement, notamment par référence à un identifiant, tel qu’un nom, un numéro d’identification, des données de localisation, un identifiant en ligne, ou à un ou plusieurs éléments spécifiques propres à son identité physique, physiologique, génétique, psychique, économique, culturelle ou sociale.',
      },
      {
        type: 'definition',
        term: 'Données à caractère personnel sensibles',
        text: 'Les données à caractère personnel dont la perte ou le Traitement illicite peut entraîner (entre autres) la stigmatisation ou l’exclusion de la Personne concernée, une atteinte à la santé, un préjudice financier ou une fraude (à l’identité).',
      },
      {
        type: 'paragraph',
        text: 'Ces catégories de données à caractère personnel comprennent en tout état de cause :',
      },
      {
        type: 'list',
        items: [
          'Les catégories particulières de données à caractère personnel',
          'Les données relatives à la situation financière ou économique de la Personne concernée',
          'Les (autres) données pouvant entraîner la stigmatisation ou l’exclusion de la Personne concernée',
          'Les noms d’utilisateur, mots de passe et autres identifiants de connexion',
          'Les données pouvant être utilisées à des fins de fraude (à l’identité)',
        ],
      },
      {
        type: 'definition',
        term: 'Traiter / Traitement',
        text: 'Toute opération ou tout ensemble d’opérations effectuées ou non à l’aide de procédés automatisés et appliquées à des données à caractère personnel ou à des ensembles de données à caractère personnel, telles que la collecte, l’enregistrement, l’organisation, la structuration, la conservation, l’adaptation ou la modification, l’extraction, la consultation, l’utilisation, la communication par transmission, la diffusion ou toute autre forme de mise à disposition, le rapprochement ou l’interconnexion, la limitation, l’effacement ou la destruction de données.',
      },
      {
        type: 'definition',
        term: 'Conditions de traitement des données',
        text: 'Les présentes conditions (qui valent également accord de sous-traitance).',
      },
      {
        type: 'definition',
        term: 'RGPD',
        text: 'Le Règlement général sur la protection des données, y compris la loi néerlandaise de mise en œuvre de ce règlement.',
      },
    ],
  },
  {
    title: '2. Champ d’application et durée',
    blocks: [
      {
        type: 'paragraph',
        text: 'Les présentes Conditions de traitement des données s’appliquent à tous les traitements effectués par Bistroo et restent en vigueur tant que Bistroo traite des données à caractère personnel. Elles ne peuvent pas être résiliées. Les obligations de confidentialité et de responsabilité continuent de s’appliquer après la fin du Contrat.',
      },
    ],
  },
  {
    title: '3. Traitement',
    blocks: [
      {
        type: 'list',
        items: [
          'Bistroo traite les données exclusivement sur instruction du Restaurant.',
          'Les finalités et les moyens du traitement sont déterminés par le Restaurant.',
          'Bistroo respecte la législation relative à la protection des données ; le Restaurant doit disposer d’une base juridique valable.',
          'L’accès aux données est limité aux collaborateurs autorisés.',
          'Bistroo peut faire appel à des Sous-traitants ultérieurs soumis aux mêmes obligations.',
          'Bistroo apporte son assistance pour les demandes des Personnes concernées.',
          'Le traitement a lieu de préférence au sein de l’EEE, à défaut avec des garanties appropriées.',
          'Les demandes des autorités font l’objet d’une évaluation juridique et sont, dans la mesure du possible, signalées au Restaurant.',
        ],
      },
    ],
  },
  {
    title: '4. Sécurité',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo prend les mesures techniques et organisationnelles appropriées. Le Restaurant peut effectuer des audits à ses propres frais.',
      },
    ],
  },
  {
    title: '5. Fuites de données',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo signale les fuites de données dans les meilleurs délais (délai visé : 48 heures). L’obligation de notification à l’autorité de contrôle et aux Personnes concernées incombe au Restaurant.',
      },
    ],
  },
  {
    title: '6. Confidentialité',
    blocks: [
      {
        type: 'paragraph',
        text: 'Bistroo ainsi que ses collaborateurs et Sous-traitants ultérieurs sont tenus au secret.',
      },
    ],
  },
  {
    title: '7. Responsabilité',
    blocks: [
      {
        type: 'paragraph',
        text: 'Le Restaurant est responsable de la licéité du traitement et garantit Bistroo contre toute réclamation en cas de violation de la législation relative à la protection des données. Les limitations prévues par le Contrat s’appliquent également ici.',
      },
    ],
  },
  {
    title: '8. Cession',
    blocks: [
      {
        type: 'paragraph',
        text: 'Le Restaurant ne peut pas céder ses droits et obligations ; Bistroo le peut.',
      },
    ],
  },
  {
    title: '9. Fin du contrat et données',
    blocks: [
      {
        type: 'paragraph',
        text: 'À la fin du Contrat, les données sont restituées ou détruites, sauf obligation légale de conservation. Les frais sont à la charge du Restaurant.',
      },
    ],
  },
  {
    title: '10. Modifications',
    blocks: [
      {
        type: 'paragraph',
        text: 'Les modifications ne sont valables que si elles ont été convenues par écrit (y compris par e-mail).',
      },
    ],
  },
  {
    title: '11. Dispositions finales',
    blocks: [
      {
        type: 'list',
        items: [
          'Bistroo fournit, sur demande, des informations sur le respect de ses obligations.',
          'Les parties coopèrent avec les autorités de contrôle.',
          'Le droit néerlandais est applicable ; les tribunaux néerlandais sont compétents.',
          'Les dispositions invalides sont remplacées par des dispositions équivalentes juridiquement valables.',
        ],
      },
    ],
  },
]

export const avg = {
  seo: {
    title: "Conditions de traitement des données (RGPD) {'|'} Bistroo",
    description:
      'Les conditions de traitement des données de Bistroo : comment nous traitons les données à caractère personnel pour le compte des restaurants, conformément au RGPD.',
  },
  title: 'Conditions de traitement des données',
  subtitle: 'Annexe 1 – Conditions de traitement des données (RGPD)',
  sections,
}
